import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Content {
  id: string;
  title: string;
  category: string;
  description?: string;
  image_url?: string;
  thumbnail_url?: string;
  content_url?: string;
  user_id: string;
  status: string;
  is_visible: boolean;
  talent_reward: number;
  created_at: string;
  view_count: number;
  like_count: number;
  comment_count: number;
  views?: number;
  price?: number;
  type?: string;
  tags: string[];
  file_urls: string[];
  profiles?: {
    full_name: string;
  };
}

interface Comment {
  id: string;
  content_id: string;
  user_id: string;
  comment: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string;
  };
  replies?: Comment[];
}

export default function ContentPage() {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMyContents, setShowMyContents] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    category: '묵상',
    description: '',
    content_url: '',
    tags: '',
    files: [] as File[]
  });
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const categories = ['전체', '묵상', '찬양', '성경공부', '간증', '설교', '기도', '영상'];

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    
    const urlLower = url.toLowerCase();
    let videoId = '';
    
    if (urlLower.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (urlLower.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    return videoId || null;
  };

  useEffect(() => {
    fetchContents();
  }, [showMyContents, user, selectedCategory, searchQuery]);

  const fetchContents = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('contents')
        .select(`
          *,
          profiles!contents_author_id_fkey (
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (showMyContents && user) {
        query = query.eq('user_id', user.id);
      } else {
        query = query.eq('status', 'published').eq('is_visible', true);
      }

      // 카테고리 필터링 (전체와 영상 제외)
      if (selectedCategory !== '전체' && selectedCategory !== '영상') {
        query = query.eq('category', selectedCategory);
      }

      // 검색 필터링
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,tags.cs.{${searchQuery}}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // 영상 카테고리인 경우 클라이언트 측에서 필터링
      let filteredData = data || [];
      if (selectedCategory === '영상') {
        filteredData = filteredData.filter(content => 
          content.content_url && getYouTubeVideoId(content.content_url)
        );
      }
      
      setContents(filteredData);
    } catch (error) {
      console.error('콘텐츠 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!uploadForm.title || !uploadForm.description) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    try {
      const fileUrls: string[] = [];
      let thumbnailUrl = '';
      let imageUrl = '';
      
      // 파일 업로드
      if (uploadForm.files.length > 0) {
        for (const file of uploadForm.files) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random()}.${fileExt}`;
          const filePath = `content-files/${user.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('contents')
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('contents')
            .getPublicUrl(filePath);

          fileUrls.push(publicUrl);
          
          // 첫 번째 이미지를 썸네일로 설정
          if (!thumbnailUrl && file.type.startsWith('image/')) {
            thumbnailUrl = publicUrl;
            imageUrl = publicUrl;
          }
          
          // 첫 번째 비디오를 썸네일로 설정 (이미지가 없을 경우)
          if (!thumbnailUrl && file.type.startsWith('video/')) {
            thumbnailUrl = publicUrl;
            imageUrl = publicUrl;
          }
        }
      }

      // content_url에서 썸네일 추출 (파일 업로드가 없을 경우)
      if (!thumbnailUrl && uploadForm.content_url) {
        const url = uploadForm.content_url.toLowerCase();
        
        // YouTube URL 처리
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
          let videoId = '';
          if (url.includes('youtube.com/watch?v=')) {
            videoId = uploadForm.content_url.split('v=')[1]?.split('&')[0];
          } else if (url.includes('youtu.be/')) {
            videoId = uploadForm.content_url.split('youtu.be/')[1]?.split('?')[0];
          }
          if (videoId) {
            thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
            imageUrl = thumbnailUrl;
          }
        }
        // Vimeo URL 처리
        else if (url.includes('vimeo.com')) {
          const videoId = uploadForm.content_url.split('vimeo.com/')[1]?.split('/')[0];
          if (videoId) {
            // Vimeo는 API를 통해 썸네일을 가져와야 하므로 기본 이미지 사용
            thumbnailUrl = uploadForm.content_url;
            imageUrl = uploadForm.content_url;
          }
        }
        // 이미지 URL 처리 (jpg, jpeg, png, gif, webp 등)
        else if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
          thumbnailUrl = uploadForm.content_url;
          imageUrl = uploadForm.content_url;
        }
        // 비디오 URL 처리 (mp4, webm, ogg 등)
        else if (url.match(/\.(mp4|webm|ogg|mov)$/i)) {
          thumbnailUrl = uploadForm.content_url;
          imageUrl = uploadForm.content_url;
        }
      }

      const tags = uploadForm.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const { error } = await supabase
        .from('contents')
        .insert({
          title: uploadForm.title,
          category: uploadForm.category,
          description: uploadForm.description,
          content_url: uploadForm.content_url,
          tags: tags,
          file_urls: fileUrls,
          image_url: imageUrl || null,
          thumbnail_url: thumbnailUrl || null,
          user_id: user.id,
          status: 'published',
          is_visible: true,
          talent_reward: 0,
          view_count: 0,
          like_count: 0,
          comment_count: 0
        });

      if (error) throw error;

      alert('콘텐츠가 업로드되었습니다.');
      setShowUploadModal(false);
      setUploadForm({
        title: '',
        category: '묵상',
        description: '',
        content_url: '',
        tags: '',
        files: []
      });
      setPreviewImages([]);
      fetchContents();
    } catch (error) {
      console.error('업로드 실패:', error);
      alert('업로드 중 오류가 발생했습니다.');
    }
  };

  const handleContentClick = async (content: Content) => {
    setSelectedContent(content);
    setShowDetailModal(true);
    
    // 조회수 증가
    await recordView(content.id);
    
    // 좋아요 상태 확인
    if (user) {
      await checkLikeStatus(content.id);
    }
    
    // 댓글 불러오기
    await fetchComments(content.id);
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const recordView = async (contentId: string) => {
    try {
      // 조회 기록 추가 (중복 방지)
      await supabase
        .from('content_views')
        .upsert({
          content_id: contentId,
          user_id: user?.id || null
        }, {
          onConflict: 'content_id,user_id'
        });

      // 조회수 증가
      const { data } = await supabase
        .from('content_views')
        .select('id')
        .eq('content_id', contentId);

      const viewCount = data?.length || 0;

      await supabase
        .from('contents')
        .update({ view_count: viewCount })
        .eq('id', contentId);

      // 로컬 상태 업데이트
      setContents(prev => prev.map(c => 
        c.id === contentId ? { ...c, view_count: viewCount } : c
      ));
    } catch (error) {
      console.error('조회수 기록 실패:', error);
    }
  };

  const checkLikeStatus = async (contentId: string) => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from('content_likes')
        .select('id')
        .eq('content_id', contentId)
        .eq('user_id', user.id)
        .single();

      setIsLiked(!!data);
    } catch (error) {
      setIsLiked(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!selectedContent) return;

    try {
      if (isLiked) {
        // 좋아요 취소
        await supabase
          .from('content_likes')
          .delete()
          .eq('content_id', selectedContent.id)
          .eq('user_id', user.id);
      } else {
        // 좋아요 추가
        await supabase
          .from('content_likes')
          .insert({
            content_id: selectedContent.id,
            user_id: user.id
          });
      }

      // 좋아요 수 업데이트
      const { data } = await supabase
        .from('content_likes')
        .select('id')
        .eq('content_id', selectedContent.id);

      const likeCount = data?.length || 0;

      await supabase
        .from('contents')
        .update({ like_count: likeCount })
        .eq('id', selectedContent.id);

      setIsLiked(!isLiked);
      setSelectedContent({ ...selectedContent, like_count: likeCount });
      setContents(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, like_count: likeCount } : c
      ));
    } catch (error) {
      console.error('좋아요 처리 실패:', error);
    }
  };

  const fetchComments = async (contentId: string) => {
    try {
      const { data, error } = await supabase
        .from('content_comments')
        .select(`
          *,
          profiles (
            full_name
          )
        `)
        .eq('content_id', contentId)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // 대댓글 불러오기
      const commentsWithReplies = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: replies } = await supabase
            .from('content_comments')
            .select(`
              *,
              profiles (
                full_name
              )
            `)
            .eq('parent_id', comment.id)
            .order('created_at', { ascending: true });

          return { ...comment, replies: replies || [] };
        })
      );

      setComments(commentsWithReplies);
    } catch (error) {
      console.error('댓글 조회 실패:', error);
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!selectedContent || !newComment.trim()) return;

    try {
      const { error } = await supabase
        .from('content_comments')
        .insert({
          content_id: selectedContent.id,
          user_id: user.id,
          comment: newComment,
          parent_id: replyTo
        });

      if (error) throw error;

      // 댓글 수 업데이트
      const { data } = await supabase
        .from('content_comments')
        .select('id')
        .eq('content_id', selectedContent.id);

      const commentCount = data?.length || 0;

      await supabase
        .from('contents')
        .update({ comment_count: commentCount })
        .eq('id', selectedContent.id);

      setNewComment('');
      setReplyTo(null);
      fetchComments(selectedContent.id);
      setSelectedContent({ ...selectedContent, comment_count: commentCount });
      setContents(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, comment_count: commentCount } : c
      ));
    } catch (error) {
      console.error('댓글 작성 실패:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('댓글을 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('content_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      if (selectedContent) {
        fetchComments(selectedContent.id);
        
        // 댓글 수 업데이트
        const { data } = await supabase
          .from('content_comments')
          .select('id')
          .eq('content_id', selectedContent.id);

        const commentCount = data?.length || 0;

        await supabase
          .from('contents')
          .update({ comment_count: commentCount })
          .eq('id', selectedContent.id);

        setSelectedContent({ ...selectedContent, comment_count: commentCount });
        setContents(prev => prev.map(c => 
          c.id === selectedContent.id ? { ...c, comment_count: commentCount } : c
        ));
      }
    } catch (error) {
      console.error('댓글 삭제 실패:', error);
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    if (!confirm('콘텐츠를 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('contents')
        .delete()
        .eq('id', contentId);

      if (error) throw error;

      alert('콘텐츠가 삭제되었습니다.');
      setShowDetailModal(false);
      fetchContents();
    } catch (error) {
      console.error('콘텐츠 삭제 실패:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadForm({ ...uploadForm, files });
    
    // 이미지 미리보기 생성
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const previews: string[] = [];
    
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result as string);
        if (previews.length === imageFiles.length) {
          setPreviewImages(previews);
        }
      };
      reader.readAsDataURL(file);
    });
    
    // 이미지가 없으면 미리보기 초기화
    if (imageFiles.length === 0) {
      setPreviewImages([]);
    }
  };

  const removePreviewImage = (index: number) => {
    const newFiles = uploadForm.files.filter((_, i) => {
      // 이미지 파일만 카운트
      const imageFiles = uploadForm.files.filter(f => f.type.startsWith('image/'));
      const imageIndex = uploadForm.files.indexOf(imageFiles[index]);
      return i !== imageIndex;
    });
    
    setUploadForm({ ...uploadForm, files: newFiles });
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  const filteredContents = contents;

  const getContentThumbnail = (content: Content) => {
    // 1. 첨부된 이미지 파일 우선
    if (content.file_urls && content.file_urls.length > 0) {
      const imageFile = content.file_urls.find(url => 
        url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
      );
      if (imageFile) return imageFile;
    }

    // 2. thumbnail_url 또는 image_url
    if (content.thumbnail_url) return content.thumbnail_url;
    if (content.image_url) return content.image_url;

    // 3. content_url에서 썸네일 추출
    if (content.content_url) {
      const url = content.content_url.toLowerCase();
      
      // YouTube
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = '';
        if (url.includes('youtube.com/watch?v=')) {
          videoId = content.content_url.split('v=')[1]?.split('&')[0];
        } else if (url.includes('youtu.be/')) {
          videoId = content.content_url.split('youtu.be/')[1]?.split('?')[0];
        }
        if (videoId) {
          return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }
      
      // 이미지 URL
      if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
        return content.content_url;
      }
    }

    // 4. AI 생성 썸네일 (제목과 설명 기반)
    const query = `${content.title} ${content.description || ''} christian faith spiritual content simple background peaceful atmosphere soft lighting minimalist design religious theme inspirational message clean aesthetic modern style warm tones serene mood`.substring(0, 200);
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28query%29%7D&width=400&height=300&seq=content-${content.id}&orientation=landscape`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              신앙 콘텐츠
            </h1>

            {/* Tab Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setShowMyContents(false)}
                className={`px-5 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  !showMyContents
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                전체
              </button>
              <button
                onClick={() => setShowMyContents(true)}
                className={`px-5 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  showMyContents
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                내 콘텐츠
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              {showMyContents ? '업로드된 콘텐츠' : '승인 완료된 콘텐츠'}
            </p>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                    <input
                      type="text"
                      placeholder="콘텐츠, 작성자, 태그 검색..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                </div>
                {user && (
                  <button 
                    onClick={() => setShowMyContents(!showMyContents)}
                    className={`px-6 py-3 font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                      showMyContents
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <i className={`${showMyContents ? 'ri-arrow-left-line' : 'ri-folder-user-line'} mr-2`}></i>
                    {showMyContents ? '전체 보기' : '내 콘텐츠'}
                  </button>
                )}
                <button 
                  onClick={() => {
                    if (!user) {
                      alert('로그인이 필요합니다.');
                      window.REACT_APP_NAVIGATE('/auth/login');
                      return;
                    }
                    setShowUploadModal(true);
                  }}
                  className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  콘텐츠 업로드
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setSearchQuery(''); // 카테고리 변경 시 검색어 초기화
                  }}
                  className={`px-5 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                <p className="text-gray-600">로딩 중...</p>
              </div>
            ) : filteredContents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {showMyContents ? '작성한 콘텐츠가 없습니다.' : '콘텐츠가 없습니다.'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredContents.map((content) => {
                  const hasYouTubeVideo = content.content_url && getYouTubeVideoId(content.content_url);
                  const hasExternalLink = content.content_url && !getYouTubeVideoId(content.content_url);
                  
                  return (
                    <div
                      key={content.id}
                      className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleContentClick(content)}
                    >
                      <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
                        <img
                          src={getContentThumbnail(content)}
                          alt={content.title}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full text-sm font-medium text-teal-600">
                          무료
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-1 bg-teal-50 text-teal-600 text-xs rounded-full">
                            {content.category}
                          </span>
                        </div>
                        <h4 
                          className="font-semibold text-gray-900 mb-2 hover:text-teal-600 transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContentClick(content);
                          }}
                        >
                          {content.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {content.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <i className="ri-eye-line"></i>
                              <span>{content.view_count || 0}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <i className="ri-heart-line"></i>
                              <span>{content.like_count || 0}</span>
                            </div>
                            {hasYouTubeVideo && (
                              <div className="flex items-center space-x-1 text-teal-600">
                                <i className="ri-play-circle-line"></i>
                              </div>
                            )}
                            {hasExternalLink && (
                              <div className="flex items-center space-x-1 text-teal-600">
                                <i className="ri-link"></i>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{new Date(content.created_at).toLocaleDateString('ko-KR')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 업로드 모달 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">콘텐츠 업로드</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setPreviewImages([]);
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  제목 *
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="콘텐츠 제목을 입력하세요"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카테고리 *
                </label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  {categories.filter(c => c !== '전체').map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  내용 *
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  placeholder="콘텐츠 내용을 입력하세요"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  태그
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="태그를 쉼표로 구분하여 입력하세요 (예: 기도, 찬양, 은혜)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  파일 첨부
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                />
                <p className="text-xs text-gray-500 mt-1">이미지, 동영상, 문서 파일을 업로드할 수 있습니다</p>
                
                {/* 이미지 미리보기 */}
                {previewImages.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">이미지 미리보기</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {previewImages.map((preview, index) => (
                        <div key={index} className="relative group">
                          <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={preview}
                              alt={`미리보기 ${index + 1}`}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePreviewImage(index)}
                            className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-red-600"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                          {index === 0 && (
                            <div className="absolute bottom-2 left-2 px-2 py-1 bg-teal-600 text-white text-xs rounded-full">
                              썸네일
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      <i className="ri-information-line mr-1"></i>
                      첫 번째 이미지가 썸네일로 사용됩니다
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  콘텐츠 URL (선택)
                </label>
                <input
                  type="url"
                  value={uploadForm.content_url}
                  onChange={(e) => setUploadForm({ ...uploadForm, content_url: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">YouTube, Vimeo 영상 링크 또는 이미지 URL을 입력하세요. 썸네일로 자동 설정됩니다.</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-amber-600 text-xl mt-0.5"></i>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">썸네일 설정 안내</p>
                    <p>• 업로드된 첫 번째 이미지/영상이 썸네일로 사용됩니다</p>
                    <p>• YouTube, Vimeo 링크를 입력하면 자동으로 썸네일이 생성됩니다</p>
                    <p>• 이미지 URL을 입력하면 해당 이미지가 썸네일로 사용됩니다</p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  업로드하기
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setPreviewImages([]);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 상세보기 모달 */}
      {showDetailModal && selectedContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full">
                    {selectedContent.category}
                  </span>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <i className="ri-eye-line"></i>
                      <span>{selectedContent.view_count || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <i className="ri-heart-line"></i>
                      <span>{selectedContent.like_count || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <i className="ri-chat-3-line"></i>
                      <span>{selectedContent.comment_count || 0}</span>
                    </div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedContent.title}</h2>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer ml-4"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* 본문 */}
            <div className="p-6">
              {/* 작성자 정보 */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedContent.profiles?.full_name || '익명'}</p>
                    <p className="text-sm text-gray-500">{new Date(selectedContent.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleLike}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isLiked
                        ? 'bg-red-100 text-red-600 hover:bg-red-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} mr-1`}></i>
                    좋아요 {selectedContent.like_count || 0}
                  </button>
                  {user?.id === selectedContent.user_id && (
                    <button
                      onClick={() => handleDeleteContent(selectedContent.id)}
                      className="px-4 py-2 bg-red-100 text-red-600 rounded-lg font-medium hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      삭제
                    </button>
                  )}
                </div>
              </div>

              {/* 태그 */}
              {selectedContent.tags && selectedContent.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedContent.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-teal-50 text-teal-600 text-sm rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 내용 */}
              <div className="prose max-w-none mb-6">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedContent.description}</p>
              </div>

              {/* YouTube 영상 임베드 */}
              {selectedContent.content_url && getYouTubeVideoId(selectedContent.content_url) && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">영상</h3>
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedContent.content_url)}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}

              {/* 첨부 파일 */}
              {selectedContent.file_urls && selectedContent.file_urls.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">첨부 파일</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedContent.file_urls.map((url, index) => {
                      // 이미지 파일인지 확인
                      const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                      
                      return isImage ? (
                        <div key={index} className="relative group">
                          <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={url}
                              alt={`첨부 이미지 ${index + 1}`}
                              className="w-full h-full object-cover object-top cursor-pointer hover:scale-110 transition-transform duration-300"
                              onClick={() => handleImageClick(url)}
                            />
                          </div>
                          {index === 0 && (
                            <div className="absolute bottom-2 left-2 px-2 py-1 bg-teal-600 text-white text-xs rounded-full">
                              썸네일
                            </div>
                          )}
                        </div>
                      ) : (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer shadow-sm"
                        >
                          <i className="ri-play-circle-line text-xl"></i>
                          <span>콘텐츠 보기</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 외부 링크 (YouTube가 아닌 경우만 표시) */}
              {selectedContent.content_url && !getYouTubeVideoId(selectedContent.content_url) && (
                <div className="mb-6">
                  <a
                    href={selectedContent.content_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer shadow-sm"
                  >
                    <i className="ri-play-circle-line text-xl"></i>
                    <span>콘텐츠 보기</span>
                  </a>
                </div>
              )}

              {/* 댓글 섹션 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-bold text-gray-900 mb-4">
                  댓글 {selectedContent.comment_count || 0}개
                </h3>

                {/* 댓글 작성 */}
                {user && (
                  <div className="mb-6">
                    {replyTo && (
                      <div className="flex items-center justify-between mb-2 p-2 bg-teal-50 rounded-lg">
                        <span className="text-sm text-teal-600">답글 작성 중...</span>
                        <button
                          onClick={() => setReplyTo(null)}
                          className="text-teal-600 hover:text-teal-700 cursor-pointer"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-teal-600"></i>
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder={replyTo ? "답글을 입력하세요..." : "댓글을 입력하세요..."}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                        />
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            className="px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                          >
                            {replyTo ? '답글 작성' : '댓글 작성'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 댓글 목록 */}
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="space-y-3">
                      {/* 댓글 */}
                      <div className="flex space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-gray-600"></i>
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold text-gray-900">{comment.profiles?.full_name || '익명'}</span>
                                <span className="text-xs text-gray-500">
                                  {new Date(comment.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                {user && (
                                  <button
                                    onClick={() => setReplyTo(comment.id)}
                                    className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer"
                                  >
                                    답글
                                  </button>
                                )}
                                {user?.id === comment.user_id && (
                                  <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="text-sm text-red-600 hover:text-red-700 cursor-pointer"
                                  >
                                    삭제
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-gray-700">{comment.comment}</p>
                          </div>
                        </div>
                      </div>

                      {/* 대댓글 */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-12 space-y-3">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="flex space-x-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-line text-gray-600 text-sm"></i>
                              </div>
                              <div className="flex-1">
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-semibold text-gray-900 text-sm">{reply.profiles?.full_name || '익명'}</span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(reply.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    {user?.id === reply.user_id && (
                                      <button
                                        onClick={() => handleDeleteComment(reply.id)}
                                        className="text-sm text-red-600 hover:text-red-700 cursor-pointer"
                                      >
                                        삭제
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-gray-700 text-sm">{reply.comment}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {comments.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    첫 댓글을 작성해보세요!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 전체화면 모달 */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={selectedImage}
            alt="전체화면 이미지"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
